import { FormEvent, useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahbcqjp14a1h3mr6hmg";

const topics = ["内容建议", "社区合作", "品牌与媒体", "问题反馈", "其他"];

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setFormError("");
      setStatus("success");
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        body.append(key, value);
      }
    });

    setStatus("submitting");
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        form.reset();
        setFormError("");
        setStatus("success");
      } else {
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后再试。",
        );
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，提交失败，请稍后再试。");
      setStatus("error");
    }
  };

  const inputClass =
    "h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
            称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className={`mt-2 ${inputClass}`}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="方便回复的邮箱"
            className={`mt-2 ${inputClass}`}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-topic" className="text-sm font-medium text-foreground-800">
          想聊的事
        </label>
        <select id="contact-topic" name="topic" defaultValue="内容建议" className={`mt-2 ${inputClass}`}>
          {topics.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="说说你的想法或建议，我们会认真看完每一条（最多 500 字）"
          className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
        />
      </div>

      <input
        type="text"
        name="website_alt"
        className="honey-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
        >
          <i className="ri-send-plane-line" />
          {status === "submitting" ? "发送中…" : "发送留言"}
        </button>
        {status === "success" && (
          <span className="flex items-center gap-1.5 text-sm text-primary-700">
            <i className="ri-checkbox-circle-line" />
            留言已提交，我们会尽快回复你。
          </span>
        )}
        {status === "error" && formError && (
          <span className="flex items-center gap-1.5 text-sm text-accent-700">
            <i className="ri-error-warning-line" />
            {formError}
          </span>
        )}
      </div>
    </form>
  );
}